import React, { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import visibility_off from '../../assets/visibility_off.png';
import visibility_on from '../../assets/visibility_on.png';
import error_icon from '../../assets/error_icon.png';
import '../../css/Login.css';
import { DataContext } from "../shared/DataContext";

const Login = () => {
    const noError = {
        message: '',
        username: false,
        password: false
    };
    const navigate = useNavigate();
    const { setCurrentUser } = useContext(DataContext);
    const [formData, setFormData] = useState({
        email: '',
        password: '',
        rememberMe: false
    });
    const [error, setError] = useState(noError);
    const [showPassword, setShowPassword] = useState(false);

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.email || !formData.password) {
            setError({
                message: 'Please fill out both the email and password fields.',
                username: true,
                password: true
            });
            return;
        }

        setError(noError);

        try {
            const response = await axios.post('/api/login', {
                email: formData.email,
                password: formData.password,
                rememberMe: formData.rememberMe
            });

            setCurrentUser(response.data);
            navigate(response.data.homepage);
        } catch (err) {
            setError({
                message: err.response?.data?.message || 'Something went wrong. Please try again.',
                username: true,
                password: true
            });
        }
    };

    const togglePasswordVisibility = () => {
        setShowPassword((prev) => !prev);
    };

    const handleForgotPassword = async () => {
        if (!formData.email) {
            setError({
                message: 'Please fill out email field.',
                username: true,
                password: false
            });
            return;
        }

        setError(noError);

        try {
            const response = await axios.post('/api/forgot_password', {
                email: formData.email
            });

            if (response.status === 200) {
                setError({
                    message: 'Please check your email for a link to reset your password.',
                    username: false,
                    password: false
                });
            } else {
                throw new Error('Something went wrong. Please try again or email us at info@mentium.pro.');
            }
        } catch (err) {
            setError({
                message: err.response?.data?.message || 'Something went wrong. Please try again or email us at info@mentium.pro.',
                username: true,
                password: false
            });
        }
    }

    return (
        <div className="loginContainer">
            <form
                className="loginForm"
                onSubmit={handleSubmit}
                noValidate
            >
                <h1 className="loginTitle">Login</h1>

                {error.message && (
                    <div className="loginErrorContainer" role="loginError">
                        <div className="loginErrorContent">
                            <div className="loginErrorIconWrapper">
                                <img
                                    loading="lazy"
                                    src={error_icon}
                                    className="loginErrorIcon"
                                    alt=""
                                />
                            </div>
                            <div className="loginErrorMessage">
                                {error.message}
                            </div>
                        </div>
                    </div>
                )}
                <div className="loginInputGroup">
                    <label
                        className={`loginInputLabel ${error.username ? 'loginInputErrorLabel' : ''}`}
                        htmlFor="email"
                    >
                        Username
                        <span className="loginRequiredAsterisk" aria-hidden="true">*</span>
                    </label>
                    <div className="loginInputWrapper">
                        <input
                            id="email"
                            type="email"
                            name="email"
                            className={`loginInput ${error.username ? 'loginInputError' : ''}`}
                            value={formData.email}
                            onChange={handleInputChange}
                            required
                            aria-required="true"
                            placeholder="Email"
                        />
                    </div>
                </div>

                <div className="loginInputGroup">
                    <label
                        className={`loginInputLabel ${error.password ? 'loginInputErrorLabel' : ''}`}
                        htmlFor="password"
                    >
                        Password
                        <span className="loginRequiredAsterisk" aria-hidden="true">*</span>
                    </label>
                    <div className="loginInputWrapper">
                        <input
                            id="password"
                            type={showPassword ? 'text' : 'password'}
                            name="password"
                            className={`loginInput ${error.password ? 'loginInputError' : ''}`}
                            value={formData.password}
                            onChange={handleInputChange}
                            required
                            aria-required="true"
                            placeholder="Password"
                        />
                        <img
                            src={showPassword ? visibility_on : visibility_off}
                            className="loginToggleIcon"
                            alt={showPassword ? 'Hide password' : 'Show password'}
                            onClick={togglePasswordVisibility}
                            aria-hidden="true"
                        />
                    </div>
                </div>

                <div className="loginRememberMeContainer">
                    <label className="loginCheckboxWrapper">
                        <input
                            type="checkbox"
                            name="rememberMe"
                            checked={formData.rememberMe}
                            onChange={handleInputChange}
                            className="loginCheckbox"
                        />
                        <span className="loginRememberMeText">Remember me</span>
                    </label>
                </div>

                <button type="submit" className="loginButton">
                    <span className="loginButtonContent">Login</span>
                </button>

                <div className="loginForgotPassword" onClick={handleForgotPassword}>
                    Forgot your password?
                </div>
            </form>
        </div>
    );
};

export default Login;